import { useContext, useEffect, useState, useCallback } from 'react';
import { CircularProgress, TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../../Context/AuthContext';
import TenantContext from '../../Context/TenantContext';
import axios from 'axios';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FaCalendarAlt } from 'react-icons/fa';
import Navbar from '../../components/Navbar';

function NurseChatAnswers() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useContext(AuthContext);
  const { tenant = {} } = useContext(TenantContext) || {};
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [noResults, setNoResults] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterOptions, setFilterOptions] = useState([{ value: 'all', label: 'All' }]);

  // Fetch dynamic assessment types from the database
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION;
    axios.get(`${baseUrl}/assessment-questions/categories`)
      .then((res) => {
        const raw = res.data;
      const cats = Array.isArray(raw) ? raw : (raw?.categories || []);
        const dynamic = cats.map((c) => ({
          value: (c.value || c.label || '').toLowerCase().replace(/\s+/g, '-'),
          label: c.label || c.value || '',
        }));
        setFilterOptions([{ value: 'all', label: 'All' }, ...dynamic]);
      })
      .catch(() => {
        // Fall back to deriving options from loaded assessments (handled below)
      });
  }, []);

  const fetchAnswers = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('[NurseChatAnswers] Not authenticated, skipping fetch');
      setLoading(false);
      return;
    }
    
    if (!token) {
      console.log('[NurseChatAnswers] No token available, skipping fetch');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      // Log current user info from AuthContext
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userObj = JSON.parse(storedUser);
          console.log('[NurseChatAnswers] Current user from localStorage:', {
            id: userObj._id,
            email: userObj.email,
            phone: userObj.phone,
            firstName: userObj.firstName,
            lastName: userObj.lastName,
            role: userObj.role
          });
        } catch (e) {
          console.error('[NurseChatAnswers] Error parsing user from localStorage:', e);
        }
      }
      
      // Use the standard patient assessments endpoint - backend handles tenant routing
      const baseUrl = import.meta.env.VITE_APP_BASE_API + import.meta.env.VITE_APP_API_VERSION;
      const endpoint = "/user/patient/nurse-chat/answers";
      
      console.log('[NurseChatAnswers] Tenant:', tenant?.id);
      console.log('[NurseChatAnswers] Fetching from:', baseUrl + endpoint);
      console.log('[NurseChatAnswers] Token present:', !!token);
      console.log('[NurseChatAnswers] Token preview:', token ? token.substring(0, 20) + '...' : 'none');
      
      const response = await axios.get(
        baseUrl + endpoint,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000 // 10 second timeout
        }
      );
      
      console.log('[NurseChatAnswers] Response status:', response.status);
      console.log('[NurseChatAnswers] Response data:', response.data);
      
      // Handle different response structures
      // Voice notes (NoteTaking) are clinician notes, not patient assessments
      const assessmentData = (response.data.assessments || response.data.data || []).filter((a) => a.type !== 'NoteTaking');
      console.log('[NurseChatAnswers] Assessment data count:', assessmentData.length);
      
      if (assessmentData.length > 0) {
        console.log('[NurseChatAnswers] Sample assessment:', assessmentData[0]);
        console.log('[NurseChatAnswers] First assessment fields:', {
          id: assessmentData[0]._id,
          name: assessmentData[0].name,
          patientEmail: assessmentData[0].patientEmail,
          patientPhone: assessmentData[0].patientPhone,
          patientId: assessmentData[0].patientId,
          timestamp: assessmentData[0].timestamp
        });
      } else {
        console.warn('[NurseChatAnswers] No assessments found for current user');
      }
      
      // Sort assessments by timestamp (newest first) using robust parser
      const sortedAssessments = assessmentData.sort((a, b) => {
        const ta = parseTimestampToDate(a.timestamp || a.createdAt)?.getTime() || 0;
        const tb = parseTimestampToDate(b.timestamp || b.createdAt)?.getTime() || 0;
        return tb - ta;
      });
      
      console.log('[NurseChatAnswers] Sorted assessments count:', sortedAssessments.length);
      
      setAssessments(sortedAssessments);
      setFilteredAssessments(sortedAssessments);
      setNoResults(sortedAssessments.length === 0);

      // Enrich filter options with any types found in data that aren't already listed
      if (sortedAssessments.length > 0) {
        setFilterOptions((prev) => {
          const existingValues = new Set(prev.map((o) => o.value));
          const extras = [];
          for (const a of sortedAssessments) {
            const raw = a.name || a.assessment_name || a.type || '';
            const val = raw.toLowerCase().replace(/\s+/g, '-');
            if (val && !existingValues.has(val)) {
              existingValues.add(val);
              extras.push({
                value: val,
                label: raw.charAt(0).toUpperCase() + raw.slice(1).replace(/-/g, ' '),
              });
            }
          }
          return extras.length ? [...prev, ...extras] : prev;
        });
      }
    } catch (error) {
      console.error("[NurseChatAnswers] Error fetching answers:", error);
      console.error("[NurseChatAnswers] Error message:", error.message);
      
      if (error.response) {
        console.error("[NurseChatAnswers] Error response data:", error.response.data);
        console.error("[NurseChatAnswers] Error status:", error.response.status);
        console.error("[NurseChatAnswers] Error headers:", error.response.headers);
      } else if (error.request) {
        console.error("[NurseChatAnswers] No response received:", error.request);
      }
      
      setNoResults(true);
      setAssessments([]);
      setFilteredAssessments([]);
    } finally {
      console.log('[NurseChatAnswers] Setting loading to false');
      setLoading(false);
    }
  }, [isAuthenticated, token, tenant?.id]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  const getAssessmentKey = (assessment) => {
    const candidate =
      assessment?.name || assessment?.assessment_name || assessment?.assessment_type || assessment?.type || '';
    const raw = typeof candidate === 'string' ? candidate : String(candidate ?? '');
    const normalized = raw.trim().toLowerCase();
    if (!normalized) return '';

    if (normalized === 'knee-arthroplasty' || normalized.includes('knee') && normalized.includes('arthroplasty')) {
      return 'knee-arthroplasty';
    }

    if (normalized === 'hip-arthroplasty' || normalized.includes('hip') && normalized.includes('arthroplasty')) {
      return 'hip-arthroplasty';
    }

    if (normalized === 'general-surgery' || normalized.includes('general') && normalized.includes('surgery')) {
      return 'general-surgery';
    }

    return normalized.replace(/\s+/g, '-');
  };

  const getAssessmentLabel = (assessment) => {
    const directName = typeof assessment?.name === 'string' ? assessment.name.trim() : '';
    if (directName) return directName;

    const key = getAssessmentKey(assessment);

    switch (key) {
      case 'knee-arthroplasty':
        return 'Knee Arthroplasty';
      case 'hip-arthroplasty':
        return 'Hip Arthroplasty';
      case 'general-surgery':
        return 'Patient Intake';
      case 'preprocedure':
        return 'Pre Procedure';
      case 'postprocedure':
        return 'Post Procedure';
      case 'woundcare':
        return 'Wound Care';
      case 'anesthesia':
        return 'Anesthesia';
      default:
        if (!key) return 'Unknown';
        return key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' ');
    }
  };

  // Parse legacy numeric or ISO timestamp to Date
  function parseTimestampToDate(ts) {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts === 'number') return new Date(ts);
    if (typeof ts === 'string') {
      const s = ts.trim();
      // Legacy formats: YYYYMMDDHHmm or YYYYMMDDHHmmss
      if (/^\d{12,14}$/.test(s)) {
        const year = parseInt(s.slice(0, 4));
        const month = parseInt(s.slice(4, 6)) - 1;
        const day = parseInt(s.slice(6, 8));
        const hours = parseInt(s.slice(8, 10));
        const minutes = parseInt(s.slice(10, 12));
        const seconds = s.length >= 14 ? parseInt(s.slice(12, 14)) : 0;
        return new Date(year, month, day, hours, minutes, seconds);
      }
      // ISO or other parseable strings
      const parsed = Date.parse(s);
      if (!isNaN(parsed)) return new Date(parsed);
    }
    return null;
  }

  // Format date as YYYY-MM-DDTHH:mm:ss.SSS±HH:MM
  function formatISOWithOffset(date) {
    if (!date) return '';
    const pad = (n, w = 2) => String(n).padStart(w, '0');
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const hh = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${y}-${m}-${d}\n${hh}:${mm}`;
  }

  function convertToHumanReadable(dateLike) {
    const d = parseTimestampToDate(dateLike);
    return formatISOWithOffset(d);
  }

  // Format date for calendar circle
  const formatDate = (dateString) => {
    const date = parseTimestampToDate(dateString) || new Date();
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' }),
    };
  };

  const handleSearch = () => {
    let filtered = assessments;

    // Apply date filter based on available dates
    if (fromDate || toDate) {
      filtered = assessments.filter((assessment) => {
        const timestamp = parseTimestampToDate(assessment.timestamp);
        if (!timestamp) return false;

        let from = null;
        let to = null;

        if (fromDate) {
          from = new Date(fromDate);
        }

        if (toDate) {
          to = new Date(toDate);
          to.setHours(23, 59, 59, 999); // Include the entire day for the end date
        } else if (fromDate) {
          // If only from date is selected, search till today
          to = new Date();
          to.setHours(23, 59, 59, 999);
        }

        // If only to date is selected, search from beginning
        if (!fromDate && toDate) {
          from = new Date(0); // Beginning of time
        }

        return timestamp >= from && timestamp <= to;
      });
    }

    // Apply assessment type filter if not 'all'
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(assessment => getAssessmentKey(assessment) === selectedFilter);
    }

    setFilteredAssessments(filtered);
    setNoResults(filtered.length === 0);
    setPage(0); // Reset to first page when filtering
  };

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setSelectedFilter('all');
    setFilteredAssessments(assessments);
    setNoResults(assessments.length === 0);
    setPage(0);
  };

  const handleCardFilter = (filterType) => {
    setSelectedFilter(filterType);

    let filtered = assessments;

    // Apply date filter based on available dates
    if (fromDate || toDate) {
      filtered = assessments.filter((assessment) => {
        const timestamp = parseTimestampToDate(assessment.timestamp);
        if (!timestamp) return false;

        let from = null;
        let to = null;

        if (fromDate) {
          from = new Date(fromDate);
        }

        if (toDate) {
          to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
        } else if (fromDate) {
          to = new Date();
          to.setHours(23, 59, 59, 999);
        }

        if (!fromDate && toDate) {
          from = new Date(0);
        }

        return timestamp >= from && timestamp <= to;
      });
    }

    // Apply assessment type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(assessment => getAssessmentKey(assessment) === filterType);
    }

    setFilteredAssessments(filtered);
    setNoResults(filtered.length === 0);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNavigate = (item) => {
    localStorage.setItem('selectedQuestion', JSON.stringify(item.data));
    localStorage.setItem('selectedAssessmentTimestamp', item.timestamp);
    navigate(`/dashboard/nurse-chat/answers/${item._id}`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="flex flex-col items-center p-5">
        <div className="w-full max-w-4xl bg-white rounded-3xl shadow-md">
          <div className="p-6">
            <h2 className="text-2xl font-semibold text-gray-900 text-center mb-6">Assessments</h2>
            
            {/* Compact Rounded Filter Toolbar */}
            <div className="w-full flex flex-col gap-3 mb-4 sm:mb-5">
              {/* Assessment Type Filter Pills with Counts */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-600 mr-1">Type:</span>
                {filterOptions.map(option => {
                  const count = option.value === 'all' 
                    ? assessments.length 
                    : assessments.filter(a => getAssessmentKey(a) === option.value).length;
                  return (
                    <button
                      key={option.value}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm ${
                        selectedFilter === option.value
                          ? 'bg-blue-500 text-white border-2 border-blue-600 shadow-md'
                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow'
                      }`}
                      onClick={() => handleCardFilter(option.value)}
                    >
                      {option.label} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Date Range Filter */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-lg sm:rounded-full p-2 shadow-sm">
                <div className="flex items-center gap-2 flex-1">
                  <FaCalendarAlt className="text-blue-500 ml-2 hidden sm:block" />
                  <input
                    type="date"
                    className="h-9 px-3 rounded-full border-2 border-gray-200 text-xs flex-1 bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    aria-label="From Date"
                  />
                  <span className="text-gray-400 font-bold">→</span>
                  <input
                    type="date"
                    className="h-9 px-3 rounded-full border-2 border-gray-200 text-xs flex-1 bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    aria-label="To Date"
                  />
                </div>

                <div className="flex gap-2 sm:ml-2">
                  <button
                    className="h-9 px-4 bg-blue-500 text-white text-xs font-medium rounded-full hover:bg-blue-600 active:scale-95 flex items-center justify-center gap-1.5 shadow-sm transition-all flex-1 sm:flex-none"
                    onClick={handleSearch}
                  >
                    🔍 Search
                  </button>
                  <button
                    className="h-9 px-4 bg-gray-400 text-white text-xs font-medium rounded-full hover:bg-gray-500 active:scale-95 shadow-sm transition-all flex-1 sm:flex-none"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Active filter chips */}
              {(selectedFilter !== 'all' || fromDate || toDate) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Active filters:</span>
                  {selectedFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 shadow-sm">
                      Type: {filterOptions.find(opt => opt.value === selectedFilter)?.label}
                      <button 
                        className="ml-0.5 text-blue-700 hover:text-blue-900 font-bold" 
                        onClick={() => handleCardFilter('all')}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {(fromDate || toDate) && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-xs font-medium rounded-full border border-green-200 shadow-sm">
                      📅 {fromDate ? new Date(fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '...'} → {toDate ? new Date(toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '...'}
                      <button 
                        className="ml-0.5 text-green-700 hover:text-green-900 font-bold" 
                        onClick={() => { setFromDate(''); setToDate(''); }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Summary cards - Compact one row design */}
            {/* <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div 
                className={`text-center rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                  selectedFilter === 'all' 
                    ? 'bg-blue-200 border-2 border-blue-400' 
                    : 'bg-blue-100 hover:bg-blue-150'
                }`}
                onClick={() => handleCardFilter('all')}
              >
                <div className="text-lg font-bold text-blue-800">{filteredAssessments.length}</div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
              <div 
                className={`text-center rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                  selectedFilter === 'preprocedure' 
                    ? 'bg-green-200 border-2 border-green-400' 
                    : 'bg-green-100 hover:bg-green-150'
                }`}
                onClick={() => handleCardFilter('preprocedure')}
              >
                <div className="text-lg font-bold text-green-800">
                  {filteredAssessments.filter(a => a.assessment_name === 'preprocedure').length}
                </div>
                <div className="text-xs text-gray-600">Pre Proc</div>
              </div>
              <div 
                className={`text-center rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                  selectedFilter === 'postprocedure' 
                    ? 'bg-purple-200 border-2 border-purple-400' 
                    : 'bg-purple-100 hover:bg-purple-150'
                }`}
                onClick={() => handleCardFilter('postprocedure')}
              >
                <div className="text-lg font-bold text-purple-800">
                  {filteredAssessments.filter(a => a.assessment_name === 'postprocedure').length}
                </div>
                <div className="text-xs text-gray-600">Post Proc</div>
              </div>
              <div 
                className={`text-center rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                  selectedFilter === 'woundcare' 
                    ? 'bg-orange-200 border-2 border-orange-400' 
                    : 'bg-orange-100 hover:bg-orange-150'
                }`}
                onClick={() => handleCardFilter('woundcare')}
              >
                <div className="text-lg font-bold text-orange-800">
                  {filteredAssessments.filter(a => a.assessment_name === 'woundcare').length}
                </div>
                <div className="text-xs text-gray-600">Wound Care</div>
              </div>
              <div 
                className={`text-center rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                  selectedFilter === 'anesthesia' 
                    ? 'bg-indigo-200 border-2 border-indigo-400' 
                    : 'bg-indigo-100 hover:bg-indigo-150'
                }`}
                onClick={() => handleCardFilter('anesthesia')}
              >
                <div className="text-lg font-bold text-indigo-800">
                  {filteredAssessments.filter(a => a.assessment_name === 'anesthesia').length}
                </div>
                <div className="text-xs text-gray-600">Anesthesia</div>
              </div>
            </div> */}

            {/* Loading indicator */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <CircularProgress color="primary" size={24} />
                  <p className="mt-4 text-gray-500">Loading assessments...</p>
                </div>
              </div>
            )}

            {/* No results message */}
            {!loading && noResults && (
              <div className="text-center py-10 bg-gray-50 rounded-lg">
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/7486/7486744.png" 
                  alt="No data" 
                  className="w-16 h-16 mx-auto mb-4 opacity-50"
                />
                <h3 className="text-xl font-medium text-gray-600 mb-1">No assessments found</h3>
                <p className="text-gray-500">
                  {fromDate || toDate
                    ? `No assessments found for the selected date range`
                    : 'No assessments are available in your history'}
                </p>
              </div>
            )}

            {/* Assessment list */}
            {!loading && !noResults && (
              <div className="overflow-hidden">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3">Date</th>
                      <th scope="col" className="px-4 py-3">Assessment Type</th>
                      <th scope="col" className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssessments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item, index) => {
                      const date = formatDate(item.timestamp);
                      return (
                        <tr 
                          key={index}
                          className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleNavigate(item)}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="w-12 h-12 rounded-full bg-blue-100 flex flex-col items-center justify-center mr-3">
                                <div className="text-sm font-bold">{date.day}</div>
                                <div className="text-xs">{date.month}</div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {convertToHumanReadable(item.timestamp)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-800">
                              {getAssessmentLabel(item)}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button className="text-blue-600 hover:text-blue-800">
                              <ChevronRightIcon />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={filteredAssessments.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NurseChatAnswers;